import Event from '../models/Event';
import moment from 'moment-timezone';

class Recurrence {
    RECUR_SINGLE    : string = 'single';
    RECUR_WEEKLY    : string = 'week';
    RECUR_MONTHLY   : string = 'month';
    RECUR_CUSTOM    : string = 'custom';
    RECUR_MULTI_DAY : string = 'multi_day';
    RECUR_FORMAT    : string = 'YYYY-MM-DDT00:00:00';
    RECUR_WEEKDAYS  : string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    startMoment: moment.Moment;
    endMoment: moment.Moment|any = null;
    customDates: any;

    frequency: string|any = '';
    weekday: number = 0;
    weeknum: number|any = '';

    constructor(event: Event) {
        this.startMoment = moment(event.date).startOf('day');

        if(event.recurrence && event.recurrence.frequency) {

            // update recurrence data in a format useful to momentjs where applicable
            switch(event.recurrence.frequency) {
                case 'weekly':
                    this.frequency = this.RECUR_WEEKLY;
                    this.weekday = this.RECUR_WEEKDAYS.indexOf(event.recurrence.weekdays[0].day);
                    break;
                case 'monthly':
                    this.frequency = this.RECUR_MONTHLY;
                    this.weekday = this.RECUR_WEEKDAYS.indexOf(event.recurrence.weekdays[0].day);
                    this.weeknum = event.recurrence.weekdays[0].num;
                    break;
                case 'custom':
                    this.frequency = this.RECUR_CUSTOM;

                if(!Array.isArray(event.recurrence.custom_dates)) {
                    event.recurrence.custom_dates = [];
                }

                  // add custom dates forcing into RECUR_FORMAT
                this.customDates = event.recurrence.custom_dates.map((customDate:any) => {
                    let customMoment = moment(customDate);
                    return customMoment.format(this.RECUR_FORMAT);
                });

                // add start date to custom date list
                this.customDates.unshift(this.startMoment.format(this.RECUR_FORMAT));

                if(this.customDates.length > 1) {
                    // sort custom dates
                    this.customDates.sort();

                    // if the first two dates match (start date and earliest customDate) remove first date
                    if(this.customDates[0] == this.customDates[1]) {
                        this.customDates.shift();
                    }
                }
                break;
            }

            this.endMoment = event.recurrence.end_date ? moment(event.recurrence.end_date) : null;
        }
        else if(event.end_date) {
            this.frequency = this.RECUR_MULTI_DAY;
            this.endMoment = moment(event.end_date);
        }
        else {
            this.frequency = this.RECUR_SINGLE;
        }
    }

    getNumSuffix(num: Number) {
        switch(num){
            case 1:
                return 'st';
            case 2:
                return 'nd';
            case 3:
                return 'rd';
            default:
                return 'th';
        }
    };

    /**
     * Returns travserse to the next/previous date in the event recurrence
     * @param {String} direction, the direction in which to traverse
     * @param {any} fromDate, date to start from
     * @param {string} format, the format to return the next date in
     * @return if found returns date in given format, else returns false
     */
    traverseDate(direction: string, fromDate: any, format: string) {
        let fromMoment = fromDate ? moment(fromDate) : moment();
        fromMoment.startOf('day');

        switch(this.frequency) {
            // SINGLE FREQUENCY
            case this.RECUR_SINGLE:
                // if 'next' and event date is after fromMoment return start date
                if(direction == 'next' && fromMoment.isBefore(this.startMoment, 'day')){
                    return this.returnFormatter(this.startMoment, format);
                }
                // if 'prev' and event date is before fromMoment return start date
                else if(direction == 'prev' && fromMoment.isAfter(this.startMoment, 'day')){
                    return this.returnFormatter(this.startMoment, format);
                }

                // else return false
                return false;

            // CUSTOM DATES: loop through dates and find first next/prev date in list higher/lower than fromMoment
            case this.RECUR_CUSTOM:
                let found = null;
                let lastDateIndex = this.customDates.length - 1;
                let fromStr = fromMoment.format(this.RECUR_FORMAT);

                // next custom date
                if(direction == 'next') {
                    for(let i=0; i <= lastDateIndex; i++) {
                        if(this.customDates[i] > fromStr) {
                            found = i;
                            break;
                        }
                    }
                }
                // previous custom date
                else {
                    for(let i=lastDateIndex; i >= 0; i--) {
                        if( this.customDates[i] < fromStr ) {
                            found = i;
                            break;
                        }
                    }
                }

            return found !== null ? this.returnFormatter(this.customDates[found], format) : false;

            // WEEKLY AND MONTHLY RECURRENCE
            case this.RECUR_WEEKLY:
            case this.RECUR_MONTHLY:

                // the recurrence date within the given month/week
                let dateMoment: any = this.getPeriodRecurrenceDate(fromMoment);

                // ensure we're not before/after the date limits
                if(direction === 'next') {
                    if(!dateMoment.isAfter(fromMoment, 'day')) {
                        dateMoment.add(1, this.frequency);

                        // when month adjust to nth week day
                        if(this.frequency === this.RECUR_MONTHLY) {
                            dateMoment = this.getPeriodRecurrenceDate(dateMoment);
                        }
                    }

                    // if before start limit, try again
                    let startLimit = this.getStartLimit();
                    if(startLimit && dateMoment.isBefore(startLimit, 'day')) {
                        dateMoment = startLimit.clone();

                        // in case start limit was the created date, get the first actual date
                        if(!this.isValidDate(dateMoment, format)) {
                            dateMoment = this.traverseDate('next', dateMoment, format);
                        }
                    }

                    // fromDate is greater than the endLimit no need to search, return false
                    let endLimit = this.getEndLimit();

                    if(endLimit && dateMoment.isAfter(endLimit, 'day')) {
                        return false;
                    }
                }
                else {
                    if(!dateMoment.isBefore(fromMoment, 'day')) {
                        dateMoment.subtract(1, this.frequency);

                        // when month adjust to nth week day
                        if( this.frequency === this.RECUR_MONTHLY ){
                            dateMoment = this.getPeriodRecurrenceDate(dateMoment);
                        }
                    }

                    // fromDate is less than the startLimit no need to search, return false
                    let startLimit = this.getStartLimit();

                    if(startLimit && dateMoment.isBefore(startLimit, 'day')) {
                        return false;
                    }
                }

                return this.returnFormatter(dateMoment, format);

            case this.RECUR_MULTI_DAY:
                return fromMoment.isBefore(this.startMoment) ? this.returnFormatter(this.startMoment, format) : false;
        }
    };

    /**
     * @param fromDate
     * @param format
     * @param startTime
     */
    getNextDate(fromDate:any, format:string = '', startTime:string = '') {
        let nextDate:any = this.traverseDate('next', fromDate, format);

        if (nextDate && startTime) {
            nextDate.set({
                'hour': startTime.substr(0, 2),
                'minute': startTime.substr(3, 2)
            })
        }

        return nextDate;
    };

    getPreviousDate(fromDate:any, format:string = '') {
        return this.traverseDate('prev', fromDate, format);
    };

    /**
     * Returns a last of recurrence dates within a given range
     * @param: dateFrom, the start of (and including) day of date range (any format accepted by momentjs)
     * @param: dateTo, the end of (and including) day of date range (any format accepted by momentjs)
     * @param: dateFormat, the format the list of dates should be in (as defined in recurrence.returnFormatter) default is 'moment'
     * @returns: array of dates in range in given format
     */
    getDatesInRange(fromDate:any, toDate:any, dateFormat: string = 'moment'){
        let fromMoment = moment(fromDate);
        let toMoment   = moment(toDate);

        let startLimit = this.getStartLimit();
        let endLimit = this.getEndLimit();

        // if fromDate is after the end of the event or toDate is before the start of the event return empty array (no dates)
        if((endLimit && fromMoment.isAfter(endLimit, 'day')) || toMoment.isBefore(startLimit, 'day')) {
            return [];
        }

        // if fromDate is earlier than the event start date use start date
        if(fromMoment.isBefore(startLimit, 'day')) {
            fromMoment = startLimit.clone();
        }

        // if toDate is later than the event max date use end date
        if(endLimit && toMoment.isAfter(endLimit, 'day')) {
            toMoment = endLimit.clone();
        }

        let dates: any[] = [];
        let traversingMoment;

        switch( this.frequency ){
        case this.RECUR_SINGLE:
            // if start date is between the two given dates then push to array
            if( this.startMoment.isBetween(fromMoment, toMoment) || this.startMoment.isSame(fromMoment) || this.startMoment.isSame(toMoment) ){
                dates.push(this.returnFormatter( this.startMoment, dateFormat));
            }
            break;

        case this.RECUR_CUSTOM:
            let fromString = fromMoment.format(this.RECUR_FORMAT);
            let toString = toMoment.format(this.RECUR_FORMAT);

            this.customDates.forEach((cd:any) => {
                if( cd >= fromString && cd <= toString ){
                    dates.push( this.returnFormatter(cd, dateFormat) );
                }
            });
            break;

        case this.RECUR_WEEKLY:
            traversingMoment = this.getPeriodRecurrenceDate(fromMoment);

            // ensure we're starting inside the date range (closestRecurrenceDate can go back a few days)
            if(traversingMoment.isBefore(fromMoment)) {
                traversingMoment.add(1, 'week');
            }

            // increment by a week and add to list until we have the past the toDate
            while(!traversingMoment.isAfter(toMoment)) {
                dates.push( this.returnFormatter(traversingMoment, dateFormat) );
                traversingMoment.add( 1, 'week' );
            }

            break;

        case this.RECUR_MONTHLY:
            traversingMoment = this.getPeriodRecurrenceDate(fromMoment);

            // ensure we're starting inside the date range (closestRecurrenceDate can go back a few days)
            if(traversingMoment.isBefore(fromMoment)){
                traversingMoment.add(1, 'month');
                traversingMoment = this.getPeriodRecurrenceDate(traversingMoment);
            }

            // increment by a month and set to recurrenceDate, then add to list until we have the past the toDate
            while(!traversingMoment.isAfter(toMoment)) {
                dates.push(this.returnFormatter(traversingMoment, dateFormat));
                traversingMoment.add(1, 'month');
                traversingMoment = this.getPeriodRecurrenceDate(traversingMoment);
            }
            break;
        }
        return dates;
    };

    /**
     * Counts how many dates are after the given date
     *
     * @param {string|moment} fromDate  default today
     * @returns {int}   returns -1 if limitless recurrence (weekly/month;y)
     */
    getTotalRemainingDates(fromDate:any|moment.Moment){
        fromDate = fromDate || moment();
        let remainingDates:any = 0;

        switch(this.frequency) {
            case this.RECUR_SINGLE:
                // if only date is today or the future, then
                remainingDates = this.isValidDate(fromDate, '') || !!this.getNextDate(fromDate, '') ? 1 : 0;
                break;

            case this.RECUR_CUSTOM:
                fromDate = this.isValidDate(fromDate, '') ? moment(fromDate) : this.getNextDate(fromDate, '');
                remainingDates = 0;

                if(fromDate) {
                    fromDate = fromDate.format(this.RECUR_FORMAT);

                    // count how many dates match or are are after fromDate
                    this.customDates.forEach((customDate:any) => {
                        if(customDate >= fromDate) {
                            remainingDates++;
                        }
                    });
                }
                break;

        case this.RECUR_MONTHLY:
        case this.RECUR_WEEKLY:
            let endLimit = this.getEndLimit();

            if(!endLimit){
                remainingDates = -1;
            }
            else if(endLimit.isBefore(fromDate)) {
                remainingDates = 0;
            }
            else {
                let remainingMoments = this.getDatesInRange(fromDate, endLimit, '');

                remainingDates = remainingMoments.length;
            }

            break;

        case this.RECUR_MULTI_DAY:
            remainingDates = moment(fromDate).isAfter( this.endMoment, 'day' ) ? 0 : 1;
            break;
        }

        return remainingDates;
    };

    /**
     * Converts the this data of 'frequency', 'weekday' and 'num' into a workable date (for weekly and monthly frequency only)
     * Used for internal calculations rather than external queries (like a private function in PHP)
     *
     * Returns recurrence date within the recurrence period,
     * Recurrence period is defined as the 'week' or 'month' the given date is in
     * eg. a recurrence of '2nd Thursday of every month' when given '2015-03-31' will return 12th of March
     * eg. a recurrence of 'Thursday of every week' when given '2015-03-31' will return 2nd of April
     *
     * @param: m moment of the given date
     * @returns moment of recurrence date
     */
    getPeriodRecurrenceDate(inputDate:moment.Moment) {
        let dateMoment = inputDate.clone();  // initial being the closest event date to fromDate (same week/month)
        dateMoment.startOf(this.frequency);

        if(dateMoment.day() != this.weekday) {
            dateMoment.day(this.weekday); // set to correct day
        }

        if(this.frequency == this.RECUR_MONTHLY) {

            // ensure we're still in the correct month (this will put us on the first {weekday} of the month
            if(dateMoment.date() > 7){
                dateMoment.add(1, 'week');
            }

            // we're already on the first {weekday} of the month so add (n-1) weeks to find the nth
            dateMoment.add(this.weeknum - 1, 'week');
        }

        return dateMoment;
    };

    getStartLimit(){
        return this.startMoment;
    };

    getEndLimit() {
        if(this.frequency === this.RECUR_CUSTOM) {
            let lastIndex = this.customDates.length - 1;
            let endMoment = moment( this.customDates[lastIndex] );
            endMoment.endOf( 'day' );

            this.endMoment = endMoment;
        }

        return this.endMoment || false;
    };

    hasRecurrence() {
        let recurs = this.frequency !== this.RECUR_SINGLE && this.frequency !== this.RECUR_MULTI_DAY;

        if(this.frequency === this.RECUR_CUSTOM && this.customDates.length === 1) {
            recurs = false;
        }

        return recurs;
    };

    isValidDate(testDate:any, insertFormat:any){
        let testMoment: any|moment.Moment = moment(testDate, insertFormat);
        let validDate = false;

        if( testMoment.isValid() ){

            switch(this.frequency){
                case this.RECUR_SINGLE:
                    validDate = testMoment.isSame( this.startMoment, 'day' );
                    break;

                case this.RECUR_MONTHLY:
                case this.RECUR_WEEKLY:

                    // invalid if outside start and end limits
                    if(testMoment.isBefore(this.getStartLimit(), 'day') || (this.getEndLimit() && testMoment.isAfter(this.getEndLimit(), 'day'))) {
                        validDate = false;
                    }
                    else if(testMoment.format( 'd' ) == this.weekday) {
                        // if recurs weekly then valid, else if monthly check if the correct nth week
                        validDate = this.frequency !== this.RECUR_MONTHLY || (Math.ceil(testMoment.format('D') / 7) == this.weeknum);
                    }
                    break;

                case this.RECUR_CUSTOM:
                    let testDateString = testMoment.format(this.RECUR_FORMAT);

                    // valid if date exists in custom dates array
                    validDate = this.customDates.indexOf(testDateString) >= 0;
                    break;
            }

        }
        return validDate;
    };

    /**
     * @returns {string|undefined}
     */
    getRecurrenceDescription() {
        let description;

        switch(this.frequency){
            case this.RECUR_CUSTOM:
                description = this.getTotalRemainingDates(moment().subtract(1, 'days')) > 1 ? 'Various Dates' : '';
                break;

            case this.RECUR_MONTHLY:
                description = 'Every ' + this.weeknum + this.getNumSuffix(this.weeknum) + ' ';
                description += this.getStartLimit().format('dddd');
                description += ' of the month';
                break;

            case this.RECUR_WEEKLY:
                description = 'Every ' + this.getStartLimit().format('dddd');
                break;

            case this.RECUR_MULTI_DAY:
                description = this.startMoment.format( 'ddd Do MMM YYYY' );
                description += ' - ';
                description += this.endMoment.format( 'ddd Do MMM YYYY' );
                break;
            }

        return description;
    };

    /**
    * Formats the a given date in the specified format
    * All query functions of the recurrence calculator use this function before returning a value
    * @param: d (moment|jsdate|string) the date to be formatted
    * @param: formatString, the format the date should be returned in
    * @return: date as specified in formatString, default is moment
    */
    returnFormatter( returnDate:moment.Moment|string, formatString:string ){
        formatString = formatString || 'moment';

        let dateMoment = moment(returnDate);

        if(!dateMoment.isValid()){
            return false;
        }

        switch(formatString){
            case 'boolean':    return true;
            case 'moment':     return dateMoment;
            case 'date':       return dateMoment.toDate();
            case 'dateString': return dateMoment.format('YYYYMMDD');
            default:           return dateMoment.format(formatString);
        }
    };
}

export default Recurrence;
